<template>
  <div class="car-content car-main-tab-content car-reduced-width">
    <back-btn @click="gobackToBookingDetail()" />
    <spinner v-if="loading" class="car-spinner car-content-spinner" />

    <div class="car-workshop-rating" data-js="workshop-rating" v-else>
      <div class="car-content-introduction" v-if="!votingReadOnly">
        <span class="car-headline">
          {{$t('workshopRating.title', {firstname: this.booking.givenname || '', lastname: this.booking.surname || ''})}}
        </span>
        <p>
          {{$t('workshopRating.description')}}
        </p>
      </div>
      <div class="car-content-introduction" v-else>
        <span class="car-headline">
          {{$t('workshopRating.titleVoted')}}
        </span>
        <p>
          {{$t('workshopRating.descriptionVoted')}}
        </p>
      </div>
      <div class="car-form-container">
        <div class="car-form-item car-form-item-taken-place">
          <label class="car-label">{{$t('workshopRating.takenPlace.label')}}*</label>
          <status-switch v-model="takenPlace" :disabled="votingReadOnly"/>
        </div>
        <div class="car-form-item car-form-item-price" v-if="takenPlace">
          <label>{{$t('workshopRating.finalPriceLabel')}}</label>
          <currency-input @setSelf="setPriceOnBlur"
                          :disabled="votingReadOnly"
                          :value="price"/>
        </div>
        <div class="car-form-item car-form-item-comment">
          <label>{{$t('workshopRating.comment')}}*</label>
          <textarea rows="3"
                    v-model="feedback"
                    :placeholder="$t('workshopRating.comment')+'*'"
                    :class="{ 'car-error-input': !$v.feedback.required && $v.feedback.$error, 'car-disabled': votingReadOnly }"
                    data-js="feedback"
                    :disabled="votingReadOnly" />
          <div class="car-error-message" v-if="!$v.feedback.required && $v.feedback.$error">
            {{$t('workshopRating.feedbackError')}}
          </div>
        </div>
        <div class="car-rating-star-container" v-if="takenPlace">
          <div class="car-section-introduction">
            <span class="car-subheadline">{{$t('workshopRating.starSectionTitle')}}</span>
            <p>{{$t('workshopRating.starSectionDescription')}}</p>
          </div>
          <div class="car-form-item car-form-item-satisfaction car-form-item-star-rating">
            <label class="car-label">{{$t('workshopRating.satisfaction')}} *</label>
            <rate :length="5" v-model="overAllSatisfaction" data-js="satisfaction" :disabled="votingReadOnly"/>
            <div class="car-error-message" v-if="!$v.overAllSatisfaction.required && $v.overAllSatisfaction.$error">
              {{$t('errors.required')}}
            </div>
          </div>
          <div class="car-form-item car-form-item-reliability car-form-item-star-rating">
            <label class="car-label">{{$t('workshopRating.reliability')}} *</label>
            <rate :length="5" v-model="reliability" data-js="reliability" :disabled="votingReadOnly"/>
          </div>
          <div class="car-error-message margin" v-if="!$v.reliability.required && $v.reliability.$error">
            {{$t('errors.required')}}
          </div>
          <div class="car-form-item car-form-item-quality car-form-item-star-rating">
            <label class="car-label">{{$t('workshopRating.quality')}} *</label>
            <rate :length="5" v-model="quality" data-js="quality" :disabled="votingReadOnly"/>
          </div>
          <div class="car-error-message margin" v-if="!$v.quality.required && $v.quality.$error">
            {{$t('errors.required')}}
          </div>
          <div class="car-form-item car-form-item-kindness car-form-item-star-rating">
            <label class="car-label">{{$t('workshopRating.kindness')}} *</label>
            <rate :length="5" v-model="kindness" data-js="kindness" :disabled="votingReadOnly"/>
          </div>
          <div class="car-error-message margin" v-if="!$v.kindness.required && $v.kindness.$error">
            {{$t('errors.required')}}
          </div>
        </div>
      </div>
      <div class="car-form-error-message" v-if="submitError">{{ $t('errors.unknown') }}</div>
      <div class="car-form-buttons car-buttons">
        <primary-btn @click="submitRating"
                     class="car-btn-primary"
                     :loading="submitLoading"
                     data-js="submit__feedback"
                     v-if="!votingReadOnly">
          {{$t('workshopRating.button')}}
        </primary-btn>
        <div class="car-form-required" v-if="!votingReadOnly">* {{ $t('catalogSelection.requiredNote') }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import Spinner from 'vue-simple-spinner';
import Rate from '@/components/service-request/Rate';
import StatusSwitch from '@/components/StatusSwitch';
import BookingApiService from '@/services/bookingApiService';
import PrimaryBtn from '@/components/PrimaryBtn';
import utils from '@/util/utils';
import BackBtn from '@/components/BackBtn';
const { required } = require('vuelidate/lib/validators');

export default {
  name: 'WorkshopRating',
  data () {
    return {
      price: null,
      overAllSatisfaction: null,
      kindness: null,
      quality: null,
      reliability: null,
      takenPlace: false,
      loading: false,
      submitLoading: false,
      requestError: false,
      submitError: false,
      votingReadOnly: false,
      feedback: ''
    }
  },
  validations() {
    let validationObj = {
      feedback: {
        required
      }
    };
    if (this.takenPlace) {
      validationObj.kindness = {
        required
      };
      validationObj.quality = {
        required
      };
      validationObj.reliability = {
        required
      };
      validationObj.overAllSatisfaction = {
        required
      };
    }
    return validationObj;
  },
  methods: {
    setPriceOnBlur(val) {
      if (!val || (val && !val.toString().replace(/[^0-9$.,]/g, ''))) {
        this.$set(this, 'price', '')
      } else {
        this.$set(this, 'price', utils.unformatCurrencyString(val));
      }
    },
    validateForm() {
      this.$v.$touch();
    },
    submitRating() {
      this.validateForm();
      if (this.$v.$invalid || this.submitLoading) {
        return;
      }

      this.submitLoading = true;
      this.submitError = false;

      const ratingInfo = {
        takenPlace: this.takenPlace,
        bookedOfferVotingTypeMap: {
          DETAIL_FRIENDLINESS: [this.kindness],
          DETAIL_QUALITY: [this.quality],
          DETAIL_RELIABILITY: [this.reliability],
          OVERALL_FEELING: [this.overAllSatisfaction]
        },
        comment: this.feedback,
        notTakenPlaceReason: this.takenPlace ? null : 'others'
      };

      if (this.price) ratingInfo.servicePrice = this.price;
      ratingInfo.locationId = this.booking.locationId;
      ratingInfo.vechile = this.booking.vehicle;
      ratingInfo.services = this.booking.services;
      BookingApiService.createBookingVoting(this.$route.params.key, ratingInfo)
        .then(() => this.$router.push({ name: 'workshopRatingSuccess' }))
        .catch(() => { this.submitError = true; })
        .finally(() => { this.submitLoading = false; })
    },
    fetchRatings() {
      return BookingApiService.getBookingVoting(this.$route.params.key).then((votingResponse) => {
        let voting = (votingResponse.data instanceof Array) ? votingResponse.data[0] || {} : votingResponse.data;

        if (voting.comment) {
          this.votingReadOnly = true;
          this.takenPlace = voting.takenPlace;
          this.kindness = voting.bookedOfferVotingTypeMap.DETAIL_FRIENDLINESS[0];
          this.quality = voting.bookedOfferVotingTypeMap.DETAIL_QUALITY[0];
          this.reliability = voting.bookedOfferVotingTypeMap.DETAIL_RELIABILITY[0];
          this.overAllSatisfaction = voting.bookedOfferVotingTypeMap.OVERALL_FEELING[0];
          this.price = voting.servicePrice;
          this.feedback = voting.comment;
        } else {
          this.votingReadOnly = false;
        }
      });
    }
  },
  created() {
    let bookedOfferKey = this.$route.params.key;
    if (bookedOfferKey) {
      this.loading = true;
      let promises = [];
      let casePromise = this.$store.dispatch('serviceCase/getCaseByBookedOfferKey', bookedOfferKey);
      let bookingDetailPromise = this.$store.dispatch('offer/getBookingDetails', bookedOfferKey);
      promises.push(this.fetchRatings(), casePromise, bookingDetailPromise);
      Promise.all(promises)
        .catch(() => { this.requestError = true; })
        .finally(() => { this.loading = false; });
    }
  },
  computed: {
    booking() {
      return this.$store.getters['offer/booking'];
    }
  },
  components: {
    PrimaryBtn,
    BackBtn,
    Spinner,
    Rate,
    StatusSwitch
  }
}
</script>
